import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { rgba } from 'polished';

import { useLocalizedNews, getLanguageCode } from '../utils';

import Grid from './Grid';
import Cell from './Cell';
import Block from './Block';
import H4 from './H4';
import H5 from './H5';
import P from './P';
import Image from './Image';
import RichTextHtml from './RichTextHtml';

const NewsCell = styled(Cell)`
  transition: 0.1s ease-in-out;
  transform-origin: center center;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
  }
`;

const NewsLink = styled(Link)`
  color: inherit;

  &:hover {
    text-decoration: none;
  }
`;

const Tag = styled.div`
  position: absolute;
  top: 0;
  left: ${({ theme }) => theme.spacing.baseDown1};
  z-index: 1;
  background-color: ${({ theme, type }) => {
    let color = theme.colors.grayDarker;

    switch (type) {
      case 'press release':
        color = theme.colors.orange;
        break;
      case 'blog':
        color = theme.colors.blueDark;
        break;
      case 'report':
        color = theme.colors.green;
        break;
      case 'event':
        color = theme.colors.blueDarker;
        break;
      default:
        break;
    }

    return rgba(color, 0.75);
  }};
`;

const NewsList = ({ selectedLocale, excludeUid, numNewsItems, withTitle }) => {
  const { news, list } = useLocalizedNews(selectedLocale, numNewsItems);
  const filteredNews = news.filter(({ node: { uid } }) => uid !== excludeUid);

  return (
    <Grid halign="center">
      {withTitle && (
        <Cell size={1} p="baseUnit">
          <H4>{list[0].node.data.title.text}</H4>
        </Cell>
      )}

      {filteredNews.length > 0 ? (
        <Grid>
          {filteredNews.map(({ node: { uid, data: item } }, i) => (
            <NewsCell
              key={`news-item-${uid}`}
              size={{ mobile: 1, tablet: i < 2 ? 1 / 2 : 1 / 3 }}
              mb={{ mobile: '0', tablet: i < 2 ? 'baseUp8' : '0' }}
              p="baseUnit"
            >
              <NewsLink
                to={`${
                  selectedLocale !== 'en-us'
                    ? `/${getLanguageCode(selectedLocale)}`
                    : ''
                }/news/${uid}`}
              >
                <Block
                  style={{
                    boxShadow: '0 0 5px rgba(0,0,0,.15)',
                    height: '100%',
                  }}
                  display="flex"
                  flexDirection="column"
                  position="relative"
                >
                  {item.type && (
                    <Tag type={item.type}>
                      <H5
                        color="white"
                        px="baseDown2"
                        py="baseDown3"
                        style={{ textTransform: 'capitalize' }}
                      >
                        {item.type}
                      </H5>
                    </Tag>
                  )}
                  <Image content={item.image} />
                  <Block p="baseUnit">
                    <H5 mb="baseDown2">{item.title.text}</H5>
                    <RichTextHtml html={item.short_description.html} />
                    <P mt="baseUnit" color="blueDark">
                      {item.read_more_text} &gt;
                    </P>
                  </Block>
                  <P
                    fontSize="baseDown1"
                    pr="baseUnit"
                    mt="auto"
                    textAlign="right"
                    color="grayDark"
                    isBold
                  >
                    {item.date}
                  </P>
                </Block>
              </NewsLink>
            </NewsCell>
          ))}
        </Grid>
      ) : (
        <Cell size={1}>
          <P p="baseUnit">{list[0].node.data.no_news.text}</P>
        </Cell>
      )}
    </Grid>
  );
};

NewsList.defaultProps = {
  excludeUid: '',
  numNewsItems: 11,
  withTitle: true,
};

NewsList.propTypes = {
  selectedLocale: PropTypes.string.isRequired,
  excludeUid: PropTypes.string,
  numNewsItems: PropTypes.number,
  withTitle: PropTypes.bool,
};

export default NewsList;
